import { useEffect, useState, type FormEvent } from 'react';
import { Navigate } from 'react-router';
import { useAuth } from '../lib/auth';
import { ApiError } from '../types/api';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Alert } from '../components/ui/Alert';
import { Card } from '../components/ui/Card';
import { MockBadge } from '../components/MockBadge';
import { APP_NAME } from '../lib/appName';
import { TOTP_LOCKED_MESSAGE, isTotpLocked } from '../lib/authErrors';
import {
  PasskeysUnsupportedError,
  fetchPasskeysAvailable,
  isPasskeyCancelled,
  signInWithPasskey,
} from '../lib/passkeys';

const RATE_LIMITED_MESSAGE = 'Too many attempts. Wait a few minutes and try again.';

/** Never says whether the account or the passkey exists. */
function passkeyErrorText(err: unknown): string {
  if (err instanceof ApiError && err.status === 429) return RATE_LIMITED_MESSAGE;
  if (err instanceof PasskeysUnsupportedError) {
    return 'This browser does not support passkeys. Log in with your email and password.';
  }
  if (isPasskeyCancelled(err)) return 'Passkey sign-in was cancelled.';
  return 'That passkey could not sign you in. Log in with your email and password.';
}

export function LoginPage() {
  const { user, loading, login, verifyTotp } = useAuth();
  const [step, setStep] = useState<'credentials' | 'totp'>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [passkeysAvailable, setPasskeysAvailable] = useState(false);

  // The API answers `enabled: false` (or nothing) when passkeys are off: no button then.
  useEffect(() => {
    let live = true;
    void fetchPasskeysAvailable().then((v) => {
      if (live) setPasskeysAvailable(v);
    });
    return () => {
      live = false;
    };
  }, []);

  if (!loading && user) return <Navigate to="/" replace />;

  async function handleCredentials(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await login(email, password);
      // 'signed_in': `user` is already set, so the `<Navigate>` above
      // takes over on the next render — no TOTP step to show.
      if (result === 'totp_required') setStep('totp');
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError(RATE_LIMITED_MESSAGE);
      } else {
        setError(
          err instanceof ApiError
            ? 'Incorrect email or password.'
            : 'Something went wrong. Try again.',
        );
      }
    } finally {
      setBusy(false);
    }
  }

  async function handlePasskey() {
    setError(null);
    setBusy(true);
    try {
      // A passkey replaces only the password: the TOTP step still follows.
      await signInWithPasskey();
      setStep('totp');
    } catch (err) {
      setError(passkeyErrorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleTotp(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const cleaned = code.replace(/\s/g, '');
      if (useRecovery) await verifyTotp(cleaned, 'recovery');
      else await verifyTotp(cleaned);
    } catch (err) {
      if (isTotpLocked(err)) {
        // The pending login is revoked server-side — start over later.
        setCode('');
        setUseRecovery(false);
        setStep('credentials');
        setError(TOTP_LOCKED_MESSAGE);
        return;
      }
      if (err instanceof ApiError && err.status === 429) {
        setError(RATE_LIMITED_MESSAGE);
        return;
      }
      setError(
        err instanceof ApiError
          ? useRecovery
            ? 'Incorrect recovery code.'
            : 'Incorrect code.'
          : 'Something went wrong. Try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg p-4">
      <Card className="w-[min(400px,100%)] p-8 flex flex-col gap-5">
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-[9px] bg-accent text-white font-bold flex items-center justify-center"
            aria-hidden="true"
          >
            N
          </div>
          <div>
            <div className="font-semibold">{APP_NAME}</div>
            <div className="text-xs text-muted">Admin login</div>
          </div>
          <span className="ml-auto">
            <MockBadge />
          </span>
        </div>

        {error ? <Alert>{error}</Alert> : null}

        {step === 'credentials' ? (
          <form className="flex flex-col gap-4" onSubmit={handleCredentials}>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="login-email">Email</Label>
              <Input
                id="login-email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="login-password">Password</Label>
              <Input
                id="login-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={busy} className="mt-2">
              {busy ? 'Logging in…' : 'Log in'}
            </Button>
            {passkeysAvailable ? (
              <>
                <div className="flex items-center gap-3 text-xs text-muted" aria-hidden="true">
                  <span className="h-px flex-1 bg-border" />
                  or
                  <span className="h-px flex-1 bg-border" />
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={busy}
                  onClick={() => void handlePasskey()}
                >
                  Sign in with a passkey
                </Button>
              </>
            ) : null}
          </form>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={handleTotp}>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="login-totp">
                {useRecovery ? 'Recovery code' : 'Code from the authenticator app'}
              </Label>
              <Input
                id="login-totp"
                {...(useRecovery
                  ? { autoComplete: 'off' }
                  : { inputMode: 'numeric' as const, autoComplete: 'one-time-code' })}
                placeholder={useRecovery ? undefined : '123 456'}
                required
                className="font-mono text-base tracking-widest"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              <button
                type="button"
                className="self-start text-xs text-accent hover:underline"
                onClick={() => {
                  setUseRecovery((v) => !v);
                  setCode('');
                  setError(null);
                }}
              >
                {useRecovery ? 'Use the authenticator app instead' : 'Use a recovery code instead'}
              </button>
            </div>
            <Button type="submit" disabled={busy} className="mt-2">
              {busy ? 'Confirming…' : 'Confirm'}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
